/* components/patterns/kitchen/KitchenBreadcrumb.jsx — крошки drawer-стека Кухни.
   [Кухня] › [Раздел] › предки… › текущий. Корень/раздел → popAll (выход в реестр);
   предок → popTo(index); текущий — не кликабелен. Имена грузятся ЛЕНИВО
   (resolveKitchenEntityName) — пока грузятся, Skeleton (не «#id», фикс боли №33).
   Новые (id='new'|'new-…') — статичная подпись без запроса.
   На xs/sm сворачивается в «‹ ближайший родитель» + текущий.
   ХЕНДОФФ → frontend/pages/kitchen/KitchenBreadcrumb.jsx. */

import React from 'react';
import { Icon } from '../../ui/Icon.jsx';
import { hooks } from '../../../hooks/index.jsx';
import { resolveKitchenEntityName } from '../../../lib/api/index.js';
import { RR } from '../../../lib/router.jsx';

  const { useState, useEffect } = React;
  const { useBreakpoint } = hooks;
  const { useSearchParams } = RR;

  // подпись раздела по типу первого элемента стека (детерминирована, не зависит от ?tab)
  const SECTION_LABEL = { card: 'Сырьё', ttk: 'ТТК', menu: 'Проекты меню' };
  // подписи для несохранённых сущностей
  function newLabel(type, id) {
    if (type === 'card') return 'Новое сырьё';
    if (type === 'menu') return 'Новый проект меню';
    if (type === 'ttk') return String(id).startsWith('new-') ? 'Новый полуфабрикат' : 'Новый рецепт';
    return 'Новый элемент';
  }
  const isNew = (id) => id === 'new' || String(id).startsWith('new-');

  // кэш разрешённых имён (между рендерами) — без мигания при back/forward
  const NAME_CACHE = new Map();

  // P1-25: имя резолвится через ИНЪЕКТИРУЕМЫЙ resolver. По умолчанию — боевой
  // resolveKitchenEntityName (lib/api: лёгкий GET карточки/ТТК/меню → name/title, ошибки → null).
  const defaultResolveName = (type, id) => resolveKitchenEntityName(type, id);

  function useEntityName(type, id, resolveName = defaultResolveName) {
    const key = type + '-' + id;
    const initial = (!type || id == null) ? null : (isNew(id) ? newLabel(type, id) : (NAME_CACHE.get(key) || null));
    const [name, setName] = useState(initial);
    useEffect(() => {
      if (!type || id == null) { setName(null); return; }
      if (isNew(id)) { setName(newLabel(type, id)); return; }
      if (NAME_CACHE.has(key)) { setName(NAME_CACHE.get(key)); return; }
      let alive = true;
      setName(null);
      Promise.resolve(resolveName(type, id)).then((n) => {
        const val = n || '#' + id;
        NAME_CACHE.set(key, val);
        if (alive) setName(val);
      }).catch(() => { if (alive) setName('#' + id); });
      return () => { alive = false; };
    }, [key]);
    return name;
  }

  // один сегмент-имя (кнопка или текущий)
  function CrumbName({ type, id, current, onClick, resolveName }) {
    const name = useEntityName(type, id, resolveName);
    if (name == null) return <span className="kt-crumb__skel skeleton" aria-label="Загрузка имени" />;
    if (current) return <span className="kt-crumb kt-crumb--current truncate" aria-current="page">{name}</span>;
    return <button type="button" className="kt-crumb kt-crumb--link truncate" onClick={onClick}>{name}</button>;
  }

  function Sep() { return <Icon name="chevron-right" size={14} className="kt-crumb__sep" />; }

  function KitchenBreadcrumb({ stack = [], onRoot, onPopTo, resolveName }) {
    const { bp } = useBreakpoint();
    const [params, setParams] = useSearchParams();
    const vParam = params.get('v');
    const clearV = () => setParams((prev) => { const sp = new URLSearchParams(prev); sp.delete('v'); return sp; });
    if (!stack.length) return null;
    const section = SECTION_LABEL[stack[0].type] || 'Продукция';
    const compact = bp === 'xs' || bp === 'sm';

    // ── Компактный режим (xs/sm): «‹ родитель» + текущий ──
    if (compact) {
      // открыт снимок версии → назад к текущей сущности (сброс &v) + крошка «Версия N»
      if (vParam) {
        return (
          <nav className="kt-breadcrumb kt-breadcrumb--compact" aria-label="Хлебные крошки">
            <button type="button" className="kt-crumb-back" onClick={clearV} aria-label="К текущей версии"><Icon name="arrow-left" size={16} /></button>
            <span className="kt-crumb-back__label truncate"><span className="kt-crumb kt-crumb--current">Версия {vParam}</span></span>
          </nav>
        );
      }
      const parentIndex = stack.length - 2; // -1 => родитель = раздел/Кухня
      const onBack = parentIndex >= 0 ? () => onPopTo && onPopTo(parentIndex) : onRoot;
      const ParentName = parentIndex >= 0
        ? <CrumbName type={stack[parentIndex].type} id={stack[parentIndex].id} onClick={onBack} resolveName={resolveName} />
        : <button type="button" className="kt-crumb kt-crumb--link" onClick={onRoot}>{section}</button>;
      return (
        <nav className="kt-breadcrumb kt-breadcrumb--compact" aria-label="Хлебные крошки">
          <button type="button" className="kt-crumb-back" onClick={onBack} aria-label="Назад">
            <Icon name="arrow-left" size={16} />
          </button>
          <span className="kt-crumb-back__label truncate">{ParentName}</span>
        </nav>
      );
    }

    // ── Полный режим (md+) ──
    return (
      <nav className="kt-breadcrumb" aria-label="Хлебные крошки">
        <button type="button" className="kt-crumb kt-crumb--root" onClick={onRoot}>
          <Icon name="kitchen" size={14} />Продукция
        </button>
        <Sep />
        <button type="button" className="kt-crumb kt-crumb--link" onClick={onRoot}>{section}</button>
        {stack.map((e, i) => {
          const isLast = i === stack.length - 1;
          return (
            <React.Fragment key={i}>
              <Sep />
              <CrumbName type={e.type} id={e.id} current={isLast && !vParam}
                onClick={isLast && vParam ? clearV : () => onPopTo && onPopTo(i)} resolveName={resolveName} />
            </React.Fragment>
          );
        })}
        {vParam && <><Sep /><span className="kt-crumb kt-crumb--current" aria-current="page">Версия {vParam}</span></>}
      </nav>
    );
  }

export { KitchenBreadcrumb };
export { useEntityName as useKitchenEntityName };
